import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/shared/Layout';
import { Card, PageHeader, Badge } from '../../components/shared/ui';
import { companyAPI } from '../../utils/api';

const LEVEL_LABEL = { junior: 'Junior', mid: 'Mid-Level', senior: 'Senior' };
const LEVEL_COLOR = { junior: '#6b7280', mid: '#2563eb', senior: '#7c3aed' };

const LockedScreen = () => (
  <Layout>
    <PageHeader title="Aday Havuzu" />
    <Card className="flex flex-col items-center justify-center py-20 text-center">
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
        Erişim Yetkiniz Bulunmamaktadır
      </h2>
      <p style={{ fontSize: 14, color: '#6b7280', maxWidth: 420, lineHeight: 1.6 }}>
        Aday havuzuna erişim bu platform üzerinden kısıtlanmıştır.
        Eşleştirilen adaylarınızı görmek için <strong>Adaylarım</strong> sayfasını kullanabilirsiniz.
      </p>
    </Card>
  </Layout>
);

const Tag = ({ children, color = '#374151', bg = '#f3f4f6' }) => (
  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: bg, color, display: 'inline-block' }}>{children}</span>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);

const DetailPanel = ({ studentId, onClose, onInvite }) => {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    companyAPI.getStudentDetail(studentId)
      .then(r => setS(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return (
    <div style={{ width: 480, borderLeft: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#9ca3af', fontSize: 13 }}>Yükleniyor...</div>
    </div>
  );
  if (!s) return null;

  const skills = Array.isArray(s.skills) ? s.skills : [];
  const certs = Array.isArray(s.certificates) ? s.certificates : [];
  const specializations = Array.isArray(s.specializations) ? s.specializations : [];
  const focusAreas = Array.isArray(s.focus_areas) ? s.focus_areas : [];
  const langs = Array.isArray(s.languages) ? s.languages : [];
  const ctf = Array.isArray(s.ctf_platforms) ? s.ctf_platforms : [];
  const projects = Array.isArray(s.projects) ? s.projects : [];
  const experiences = Array.isArray(s.work_experiences) ? s.work_experiences : [];
  const awards = Array.isArray(s.awards) ? s.awards : [];

  return (
    <div style={{ width: 520, borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflowY: 'auto', background: '#fff' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{s.first_name} {s.last_name}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{s.university}{s.department ? ` — ${s.department}` : ''}</div>
          {s.email && <div style={{ fontSize: 12, color: '#9ca3af' }}>{s.email}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
          {s.llm_level && (
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#ede9fe', color: LEVEL_COLOR[s.llm_level] || '#6b7280', fontWeight: 700 }}>
              {LEVEL_LABEL[s.llm_level] || s.llm_level}
            </span>
          )}
        </div>
      </div>

      <div style={{ padding: '16px 24px', flexGrow: 1 }}>
        {/* Kişisel */}
        <Section title="👤 Kişisel Bilgiler">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 13 }}>
            {[
              ['Şehir', s.city], ['GPA', s.gpa], ['Sınıf', s.grade ? `${s.grade}. Sınıf` : null],
              ['Telefon', s.phone], ['Cinsiyet', s.gender], ['Uyruk', s.nationality],
              ['Askerlik', s.military_status],
            ].map(([l, v]) => v ? (
              <div key={l}><span style={{ color: '#9ca3af' }}>{l}: </span><span style={{ color: '#374151', fontWeight: 500 }}>{v}</span></div>
            ) : null)}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
            {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb' }}>🔗 LinkedIn</a>}
            {s.github_url && <a href={s.github_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#374151' }}>🐙 GitHub</a>}
          </div>
        </Section>

        {/* Hakkında */}
        {s.about_me && (
          <Section title="💬 Hakkında">
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{s.about_me}</p>
          </Section>
        )}

        {/* AI Analizi */}
        {s.llm_summary && (
          <div style={{ background: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>✦ AI CV Analizi</div>
            <p style={{ fontSize: 12, color: '#4c1d95', lineHeight: 1.6, margin: '0 0 10px' }}>{s.llm_summary}</p>
            {Array.isArray(s.llm_strengths) && s.llm_strengths.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#065f46', marginBottom: 4 }}>Güçlü Yanlar</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {s.llm_strengths.map((str, i) => <Tag key={i} color="#065f46" bg="#d1fae5">+ {str}</Tag>)}
                </div>
              </div>
            )}
            {Array.isArray(s.llm_weaknesses) && s.llm_weaknesses.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Gelişim Alanları</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {s.llm_weaknesses.map((w, i) => <Tag key={i} color="#92400e" bg="#fef3c7">△ {w}</Tag>)}
                </div>
              </div>
            )}
            {s.cv_keyword_score != null && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#7c3aed' }}>CV Anahtar Kelime Puanı: <strong>{s.cv_keyword_score}/100</strong></div>
            )}
          </div>
        )}

        {/* Beceriler */}
        {skills.length > 0 && (
          <Section title="🛠️ Teknik Beceriler">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {skills.map((sk, i) => <Tag key={i} color="#1d4ed8" bg="#eff6ff">{sk}</Tag>)}
            </div>
          </Section>
        )}

        {/* Uzmanlık */}
        {specializations.length > 0 && (
          <Section title="🎯 Uzmanlık Alanları">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {specializations.map((s, i) => <Tag key={i} color="#7c3aed" bg="#ede9fe">{s}</Tag>)}
            </div>
          </Section>
        )}

        {/* Odak Alanları */}
        {focusAreas.length > 0 && (
          <Section title="🔍 Odak Alanları">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {focusAreas.map((f, i) => <Tag key={i} color="#374151" bg="#f3f4f6">{f}</Tag>)}
            </div>
          </Section>
        )}

        {/* Sertifikalar */}
        {certs.length > 0 && (
          <Section title="🏆 Sertifikalar">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {certs.map((c, i) => <Tag key={i} color="#065f46" bg="#d1fae5">{c}</Tag>)}
            </div>
          </Section>
        )}

        {/* CTF */}
        {ctf.length > 0 && (
          <Section title="🚩 CTF Platformları">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {ctf.map((c, i) => <Tag key={i} color="#374151" bg="#f3f4f6">{c}</Tag>)}
            </div>
          </Section>
        )}

        {/* Diller */}
        {langs.length > 0 && (
          <Section title="🌍 Dil Bilgisi">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {langs.map((l, i) => <Tag key={i} color="#374151" bg="#f3f4f6">{l.language} — {l.level}</Tag>)}
            </div>
          </Section>
        )}

        {/* Projeler */}
        {projects.length > 0 && (
          <Section title="🚀 Projeler">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {projects.map((p, i) => (
                <div key={i} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{p.description}</div>}
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#2563eb' }}>🔗 {p.url}</a>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* İş Deneyimi */}
        {experiences.length > 0 && (
          <Section title="💼 İş Deneyimi">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {experiences.map((e, i) => (
                <div key={i} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{e.title} — {e.company}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{e.start_date}{e.end_date ? ` → ${e.end_date}` : ' → Devam ediyor'}</div>
                  {e.description && <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>{e.description}</div>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Ödüller */}
        {awards.length > 0 && (
          <Section title="🥇 Ödüller & Başarılar">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {awards.map((a, i) => <Tag key={i} color="#92400e" bg="#fef3c7">{a}</Tag>)}
            </div>
          </Section>
        )}

        {/* Staj Tercihleri */}
        <Section title="📋 Staj Tercihleri">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
            {[
              ['Çalışma Şekli', s.work_type_preference],
              ['Staj Türü', s.internship_type_preference],
              ['Dönem', s.internship_period_preference],
            ].map(([l, v]) => v ? (
              <div key={l}><span style={{ color: '#9ca3af' }}>{l}: </span><span style={{ color: '#374151' }}>{v}</span></div>
            ) : null)}
          </div>
        </Section>
      </div>

      {/* Davet butonu */}
      <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #f3f4f6' }}>
        <button onClick={() => onInvite({ id: studentId, first_name: s.first_name, last_name: s.last_name })}
          style={{ width: '100%', padding: '10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          ✉️ Staja Davet Et
        </button>
      </div>
    </div>
  );
};

const InviteModal = ({ student, positions, onClose, onSent }) => {
  const [positionId, setPositionId] = useState(positions[0]?.id || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!positionId) return;
    setSending(true);
    try {
      await companyAPI.inviteStudent({ student_id: student.id, position_id: positionId, message });
      onSent();
    } catch { }
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Staja Davet Et</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>{student.first_name} {student.last_name}</p>
        <select value={positionId} onChange={e => setPositionId(e.target.value)}
          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {positions.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Kişisel mesaj (isteğe bağlı)"
          rows={3} style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>İptal</button>
          <button onClick={send} disabled={sending || !positionId}
            style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            {sending ? 'Gönderiliyor...' : 'Davet Gönder'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CompanyStudents = () => {
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState({ name: '', university: '', skill: '', grade: '', gpa_min: '' });
  const [selected, setSelected] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);
  const [positions, setPositions] = useState([]);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const load = useCallback((params = {}) => {
    setLoading(true);
    companyAPI.getStudents(params)
      .then(r => { setFeatureEnabled(true); setStudents(r.data); })
      .catch(err => {
        if (err?.response?.data?.code === 'feature_disabled') setFeatureEnabled(false);
        else setFeatureEnabled(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    companyAPI.getPositions().then(r => setPositions(r.data)).catch(() => {});
  }, [load]);

  if (featureEnabled === false) return <LockedScreen />;
  if (featureEnabled === null) return <Layout><PageHeader title="Aday Havuzu" /></Layout>;

  const inp = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff' };

  return (
    <Layout>
      <PageHeader title="Aday Havuzu" />

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#111827', color: '#fff', padding: '12px 20px', borderRadius: 10, zIndex: 200, fontSize: 13 }}>{toast}</div>
      )}

      {/* Filtreler */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 10 }}>
          {[
            ['Ad / Soyad', 'name'], ['Üniversite', 'university'], ['Beceri', 'skill'],
          ].map(([ph, key]) => (
            <input key={key} placeholder={ph} value={search[key]} onChange={e => setSearch(s => ({ ...s, [key]: e.target.value }))}
              style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          ))}
          <select value={search.grade} onChange={e => setSearch(s => ({ ...s, grade: e.target.value }))} style={{ ...inp }}>
            <option value="">Sınıf (tümü)</option>
            {[1,2,3,4,5].map(g => <option key={g} value={g}>{g}. Sınıf</option>)}
          </select>
          <input placeholder="Min GPA" type="number" step="0.1" value={search.gpa_min} onChange={e => setSearch(s => ({ ...s, gpa_min: e.target.value }))}
            style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => { setSearch({ name: '', university: '', skill: '', grade: '', gpa_min: '' }); load(); }}
            style={{ padding: '8px 14px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Temizle</button>
          <button onClick={() => load(Object.fromEntries(Object.entries(search).filter(([,v]) => v)))}
            style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Ara</button>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', minHeight: 500 }}>
        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto', borderRight: selected ? '1px solid #e5e7eb' : 'none' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Yükleniyor...</div>
          ) : students.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Öğrenci bulunamadı.</div>
          ) : students.map(s => (
            <div key={s.id} onClick={() => setSelected(s.id)}
              style={{ padding: '14px 18px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: selected === s.id ? '#f5f3ff' : '#fff', transition: 'background 0.1s' }}
              onMouseEnter={e => { if (selected !== s.id) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { if (selected !== s.id) e.currentTarget.style.background = '#fff'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{s.first_name} {s.last_name}</span>
                  {s.llm_level && (
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: '#ede9fe', color: LEVEL_COLOR[s.llm_level], fontWeight: 700, marginLeft: 8 }}>
                      {LEVEL_LABEL[s.llm_level]}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {s.gpa && <span style={{ fontSize: 11, color: '#6b7280' }}>GPA {s.gpa}</span>}
                  {s.cv_keyword_score != null && <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 20, background: '#eff6ff', color: '#2563eb' }}>{s.cv_keyword_score}/100</span>}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                {s.university}{s.department ? ` — ${s.department}` : ''}{s.grade ? ` · ${s.grade}. Sınıf` : ''}
              </div>
              {Array.isArray(s.skills) && s.skills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {s.skills.slice(0, 5).map((sk, i) => <Tag key={i} color="#1d4ed8" bg="#eff6ff">{sk}</Tag>)}
                  {s.skills.length > 5 && <Tag color="#6b7280" bg="#f3f4f6">+{s.skills.length - 5}</Tag>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Detay Panel */}
        {selected && (
          <DetailPanel
            studentId={selected}
            onClose={() => setSelected(null)}
            onInvite={(student) => setInviteFor(student)}
          />
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>{students.length} aday listeleniyor</div>

      {inviteFor && (
        <InviteModal
          student={inviteFor}
          positions={positions}
          onClose={() => setInviteFor(null)}
          onSent={() => { setInviteFor(null); showToast('✅ Davet başarıyla gönderildi.'); }}
        />
      )}
    </Layout>
  );
};

export default CompanyStudents;
